import React, { useEffect, useState, useContext } from "react";
import { motion } from "framer-motion";
import { Formik } from "formik";
import logo from "../../images/LogoOnlyAnimation.gif";
import logoOre from "../../images/OrevanLogo.png";
import "./Login.css";
import BackgroundAnimated from "../BackgroundAnimated/BackgroundAnimated";
import { useNavigate } from "react-router-dom";
import { login } from "../../api/authApi";
import { saveToken } from "../../utils/storage";
import AuthContext from "../../context/AuthContext";
import LoadingScreen from "../LoadingScreen/LoadingScreen";

function Login() {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [errorEmail, setErrorEmail] = useState("");
  const [gradientPosition, setGradientPosition] = useState({ x: 100, y: 100 });
  const [isLoading, setIsLoading] = useState(false);
  const [showInitialLoading, setShowInitialLoading] = useState(true);
  const navigate = useNavigate();
  const { state, dispatch } = useContext(AuthContext);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowInitialLoading(false);
    }, 8000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (state.isAuthenticated) {
      navigate("/");
    }
  }, [state.isAuthenticated, navigate]);

  useEffect(() => {
    let body = document.body;
    body.style.backgroundColor = "#3B1729";
  }, []);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 200;
    const y = ((e.clientY - rect.top) / rect.height) * 200;
    setGradientPosition({ x, y });
  };

  const handlePasswordNavigate = () => {
    navigate("/forgot-password");
  };

  const togglePassword = () => {
    setShowPassword(!showPassword);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setErrorEmail("");

    // Basic validation
    if (!email) {
      setErrorEmail("Email is required");
      return;
    }
    if (!password) {
      setError("Password is required");
      return;
    }

    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorEmail("Please enter a valid email address");
      return;
    }

    setIsLoading(true);
    try {
      const response = await login({ email, password });
      if (response.status === 'success') {
        saveToken(response.data.token);
        dispatch({ 
          type: 'LOGIN', 
          payload: {
            user: response.data.user,
            token: response.data.token
          }
        });
        navigate("/");
      } else {
        setError(response.error.message);
      }
    } catch (error) {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {showInitialLoading ? (
        <LoadingScreen />
      ) : (
        <div className="relative overflow-hidden">
          <BackgroundAnimated />
          <div className="h-screen w-screen flex justify-center ">
            <motion.div
              initial={{
                opacity: 0,
              }}
              animate={{
                opacity: 1,
              }}
              transition={{
                duration: 2,
                delay: 0.3,
              }}
              className="min-h-screen flex items-center lg:items-start justify-center  bg-darkPurple lg:w-2/5 rounded-3xl relative md:z-10"
            >
              <div className=" text-white p-6  w-full md:max-w-lg lg:max-w-2xl">
                <div className="relative z-10">
                  <div className="flex justify-center ml-4 lg:ml-0 md:mb-3">
                    <img src={logo} className="w-2/5  md:w-1/3" alt="" />
                  </div>
                  <p className="text-3xl font-bold text-center md:mb-8 md:mt-4">
                    Sign In to ORVI
                  </p>
                  <div className="my-4">
                    <Formik>
                      {() => (
                        <form onSubmit={handleSubmit}>
                          <div>
                            <label className="text-lg font-medium my-3">
                              Email
                            </label>
                            <input
                              onChange={(e) => {
                                setEmail(e.target.value);
                                setErrorEmail("");
                              }}
                              name="email"
                              id="email"
                              className="w-full rounded-lg p-4 bg-transparentPurple text-grayColor placeholder-grayColor my-2"
                              type="text"
                              placeholder="example@gmail.com"
                            />

                            <span className="text-red-600">{errorEmail}</span>
                          </div>
                          <div>
                            <div className="mt-2 mb-1 flex justify-between items-center">
                              <label className="text-lg font-medium ">
                                Password
                              </label>

                              <button
                                onClick={handlePasswordNavigate}
                                type="button"
                                className="text-lightPurple underline "
                              >
                                Forgot?
                              </button>
                            </div>
                            <div className="relative w-full">
                              <input
                                onChange={(e) => {
                                  setPassword(e.target.value);
                                  setError("");
                                }}
                                type={showPassword ? "text" : "password"}
                                name="password"
                                id="password"
                                placeholder="Enter Your Password.."
                                className="w-full  relative p-4 bg-transparentPurple text-grayColor placeholder-grayColor rounded-lg my-2"
                              />

                              <i
                                className={`fa-regular ${showPassword ? "fa-eye" : "fa-eye-slash"
                                  } absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer`}
                                onClick={togglePassword}
                              ></i>
                            </div>
                            <span className="text-red-600">{error}</span>
                          </div>

                          <div className="my-4 lg:flex lg:flex-col lg:justify-between  lg:h-48 2xl:h-80">
                            <div>
                              <motion.button
                                onMouseMove={handleMouseMove}
                                animate={{
                                  backgroundPosition: `${gradientPosition.x}% ${gradientPosition.y}%`,
                                }}
                                type="submit"
                                disabled={isLoading}
                                className="text-white py-3 rounded-lg font-bold w-full hover:translate-x-[2px] hover:translate-y-[3px] transition-all duration-75 disabled:opacity-70 disabled:cursor-not-allowed"
                                style={{
                                  backgroundImage:
                                    "radial-gradient(circle, #6984DA, #B66B8D)",
                                  backgroundSize: "300% 300%",
                                  transition: "background-position 0.2s ease",
                                }}
                                transition={{
                                  duration: 1,
                                }}
                              >
                                <span>
                                  {isLoading ? (
                                    <i className="fas fa-spinner fa-spin mr-2"></i>
                                  ) : (
                                    "Sign In"
                                  )}
                                </span>
                              </motion.button>
                            </div>

                            <div className="lg:mb-12 2xl:mb-0">
                              <img
                                className="w-2/5  md:w-1/3 2xl:w-30 mx-auto mt-4 "
                                src={logoOre}
                                alt=""
                              />
                            </div>
                          </div>
                        </form>
                      )}
                    </Formik>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </>
  );
}

export default Login;
